import React, {
	useState,
	useMemo,
	useEffect,
} from 'react';
import { SorterResult } from 'antd/es/table/interface';
import { useLocation } from 'react-use';
import {
	FixtureStates,
	FixtureTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import {
	GetVoyagesResponse,
	VoyageResponse,
} from '@api/features/voyages/getVoyages';
import Table from '@client/components/Table/Table';
import { getVoyages } from '@client/lib/api';
import Card from '@client/components/Card/Card';
import SimpleScreen from '@client/components/screens/SimpleScreen';
import { Links } from '@client/utils/links';
import { useTableQuery } from '@client/utils/hooks/useTableQuery';
import { useAuth } from '@client/lib/auth';
import { getVoyageListColumns } from './voyageListTableHelpers';
import styles from './VoyagesListScreen.module.css';

type FilterMaps = {
	vessels: string[];
	identifiers: string[];
	charterers: string[];
	fixedBy: number[];
	operatedBy: number[];
	statuses: Values<typeof FixtureStates>[];
	types: Values<typeof FixtureTypes>[];
};

export const CARGO_RELET_FILTER = 'cargoRelet';

const VoyagesListScreen = () => {
	const [activeTab, setActiveTab] = useState('active');
	const { userIndex, userInfo } = useAuth();

	const personalVesselCacheKey = userInfo.filterVessels ?
		userInfo.relevantVessels.map((v) => v.id).sort((a, b) => a - b).join(',') :
		'all';

	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const filters = params.get('filter');
	const voyageTypeFilter = filters ?
		(filters.split(',') as Array<Values<typeof FixtureTypes>>) :
		undefined;

	const {
		data: voyages,
		refetch,
		isLoading,
		error,
		pagination,
		setSearch,
		setPagination,
		onTableChange,
	} = useTableQuery<GetVoyagesResponse>({
		queryKey: ['voyages', activeTab, voyageTypeFilter, userInfo.filterVessels, personalVesselCacheKey],
		fetcher: ({ pagination, filters, sorter, search, }) => {
			const s = Array.isArray(sorter) ? sorter[0] : sorter;

			const rawField = (s?.field ?? s?.columnKey);

			const mappedField =
				rawField === 'fixedBy' ? 'fixedBySortName' :
					rawField === 'operatedBy' ? 'operatedBySortName' :
						rawField;

			const mappedSorter =
				s?.order && typeof mappedField === 'string' ?
					({ ...s, field: mappedField, columnKey: mappedField } as SorterResult<VoyageResponse>) :
					undefined;

			const normalizedFilters = (
				typeof filters === 'object' && filters != null ?
					filters :
					{}
			) as Record<string, any>;

			const typeFilters = normalizedFilters.fixtureType ?? [];

			const cargoReletSelected = typeFilters.includes(CARGO_RELET_FILTER);

			const mappedFilters = {
				...normalizedFilters,
				fixtureType: typeFilters.filter((v: string) => v !== CARGO_RELET_FILTER),
				...(cargoReletSelected && {
					hasCargoRelet: true,
				}),
			};

			return getVoyages({
				includeCompleted: activeTab === 'completed',
				pagination,
				sorter: mappedSorter,
				search,
				filters: mappedFilters,
				voyageTypeFilter,
			});
		},
		defaultPageSize: 100,
	});

	useEffect(() => {
		refetch();
	}, []);

	const filterMaps = useMemo<FilterMaps>(() => {
		const vessels = new Set<string>();
		const identifiers = new Set<string>();
		const charterersSet = new Set<string>();
		const fixedBySet = new Set<number>();
		const operatedBySet = new Set<number>();
		const statusesSet = new Set<Values<typeof FixtureStates>>();
		const typesSet = new Set<Values<typeof FixtureTypes>>();

		(voyages?.rows ?? []).forEach((v) => {
			if (v.vesselName) {
				vessels.add(v.vesselName);
			}

			if (v.identifier) {
				identifiers.add(v.identifier);
			}

			if (v.fixedBy != null) {
				fixedBySet.add(v.fixedBy);
			}

			if (v.operatedBy != null) {
				operatedBySet.add(v.operatedBy);
			}

			if (v.state) {
				statusesSet.add(v.state);
			}

			if (v.fixtureType) {
				typesSet.add(v.fixtureType);
			}

			(v.cargos ?? []).forEach((c) => {
				const name = c?.chartererName?.trim();

				if (name) {
					charterersSet.add(name);
				}
			});

			if (v.counterpartyName) {
				charterersSet.add(v.counterpartyName);
			}
		});

		return {
			vessels: [...vessels].sort(),
			identifiers: [...identifiers].sort(),
			charterers: [...charterersSet].sort(),
			fixedBy: [...fixedBySet],
			operatedBy: [...operatedBySet],
			statuses: [...statusesSet],
			types: [...typesSet],
		};
	}, [voyages]);

	const userNameById = useMemo(() => {
		const map = new Map<number, string>();
		for (const [id, u] of userIndex.entries()) {
			map.set(id, u?.name ?? `#${id}`);
		}

		return map;
	}, [userIndex]);

	const fixedByFilters = useMemo(
		() => filterMaps.fixedBy
			.map((id) => ({ text: userNameById.get(id) ?? `#${id}`, value: id }))
			.sort((a, b) => a.text.localeCompare(b.text)),
		[filterMaps.fixedBy, userNameById],
	);

	const operatedByFilters = useMemo(
		() => filterMaps.operatedBy
			.map((id) => ({ text: userNameById.get(id) ?? `#${id}`, value: id }))
			.sort((a, b) => a.text.localeCompare(b.text)),
		[filterMaps.operatedBy, userNameById],
	);

	const columns = useMemo(
		() => getVoyageListColumns({
			includeFilters: true,
			filterMaps: {
				...filterMaps,
				fixingCompanies: userInfo.fixingCompanies.map((c) => c.name),
				fixedByFilters,
				operatedByFilters,
			},
		}),
		[filterMaps, userInfo.fixingCompanies, fixedByFilters, operatedByFilters],
	);

	return (
		<SimpleScreen
			title="Voyages"
			error={error as Error}
			rootPageTitle="Voyages"
		>
			<Card
				slim
				onTabChange={(key) => {
					setActiveTab(key);
					setPagination({ current: 1, pageSize: pagination.pageSize });
				}}
				activeTabKey={activeTab}
				tabList={[
					{
						tab: 'Active',
						key: 'active',
					},
					{
						tab: 'Completed',
						key: 'completed',
					},
				]}
			>
				<div className={styles.voyagesTable}>
					<Table<VoyageResponse>
						showSearch
						onSearch={setSearch}
						getRowHref={(row) => Links.Voyage.get(row.id)}
						pagination={{
							current: pagination.current,
							pageSize: pagination.pageSize,
							total: voyages?.count ?? 0,
							pageSizeOptions: [50, 100, 200, 500],
						}}
						onChange={onTableChange}
						loading={isLoading}
						size="small"
						dataSource={voyages?.rows ?? []}
						columns={columns}
						scroll={{ x: 1380 }}
						tableLayout="fixed"
					/>
				</div>
			</Card>
		</SimpleScreen>
	);
};

export default VoyagesListScreen;
